import React, {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
} from 'react'

import type { ReactAnchorProps } from '../types'

type LinkContextValue = ComponentType<ReactAnchorProps>

const LinkContext = createContext<LinkContextValue>(DefaultLinkComponent)

export function DefaultLinkComponent({ children, ...props }: ReactAnchorProps) {
  return <a {...props}>{children}</a>
}

type Props = {
  children: ReactNode
  /**
   * A React component that renders a link. This component should implement whatever logic
   * is necessary to handle routing in your application.
   */
  linkComponent: ComponentType<ReactAnchorProps> | null
}

/**
 * `LinkProvider` allows an app using the design system to specify how all
 * links rendered by the design system should work. The design system will use
 * the provided component to render all links, including "regular" links like
 * those inside an `UnstyledAnchor` or `Chip`, as well as more complex links
 * like those used for each row of an `AnchorTable`. You might want to use this
 * if you have a custom routing library you want to allow to render your links
 * (e.g. react-router), or if you want to ensure that all external links open
 * in a new tab.
 *
 * Your custom component generally should not specify any visual styling for
 * the resulting anchor. It should provide only custom behavior, leaving all
 * visual styling up to the design system.
 */
export function LinkProvider(props: Props) {
  const { children, linkComponent } = props
  return (
    <LinkContext.Provider value={linkComponent || DefaultLinkComponent}>
      {children}
    </LinkContext.Provider>
  )
}

export function useLinkComponent() {
  return useContext(LinkContext)
}
